import { format } from 'date-fns'
import { enGB } from 'date-fns/locale'

/**
 * Utility functions for the application
 */

/** Formats: "15/03/2024" */
export function formatDate(date: Date | string): string {
  return format(new Date(date), 'dd/MM/yyyy')
}

/** Formats: "15/03/2024, 14:30" */
export function formatDateTime(date: Date | string): string {
  return format(new Date(date), 'dd/MM/yyyy, HH:mm')
}

/** Formats: "15/03/2024, 14:30:45" */
export function formatDateTimeSeconds(date: Date | string): string {
  return format(new Date(date), 'dd/MM/yyyy, HH:mm:ss')
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

type DailyCutoff = {
  cutoff_time: string
}

type CutoffTimesByDay = Partial<Record<DayOfWeek, DailyCutoff>>

const LONDON_TIMEZONE = 'Europe/London'

/**
 * Returns today's cutoff time in London timezone as "h:mm a".
 */
export function getFormattedTodayCutoffTime(cutoffTimes: CutoffTimesByDay): string {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: LONDON_TIMEZONE }))
  const currentDay = format(now, 'EEEE').toLowerCase() as DayOfWeek
  const todayCutoff = cutoffTimes[currentDay]!.cutoff_time

  const [cutoffHours, cutoffMinutes] = todayCutoff.split(':')
  const cutoffTime = new Date(now)
  cutoffTime.setHours(Number.parseInt(cutoffHours, 10), Number.parseInt(cutoffMinutes, 10), 0)

  return format(cutoffTime, 'h:mm a', { locale: enGB })
}

/**
 * Smoothly scrolls to a section on the page by its ID
 * @param sectionId - The ID of the element to scroll to
 * @param offset - The offset from the top of the element (default: 100px)
 */
export function scrollToSection(sectionId: string, offset = 100): void {
  const element = document.getElementById(sectionId)
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
    window.scrollTo({
      top: elementPosition - offset,
      behavior: 'smooth'
    })
  }
}

/**
 * Calculates the age from a date of birth string
 * @param dob - The date of birth string in the format of MM/DD/YYYY
 * @returns The age
 */
export function calculateAgeFromDobString(dob: string): number {
  const birthDate = new Date(dob)
  const today = new Date()

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

/**
 * Type for individual SCR data items (medications, observations, etc.)
 */
export type SCRDataItem = {
  date: string
  observation?: string
  medication_time?: string
  dosage?: string
  quantity?: string
}

/**
 * Type for parsed SCR data structure
 */
export type ParsedSCRData = {
  lastUpdated: Date | null
  error?: string
} & Record<string, string[] | SCRDataItem[] | Date | string | null | undefined>

/**
 * Extracts and parses the SCR data from a raw JSON string.
 * @param summaryCareRecord - The raw SCR JSON string from the API
 * @param summaryCareRecordUpdatedAt - Optional override for the last updated timestamp
 * @returns Parsed SCR data or null if invalid/unavailable
 */
export function parseSummaryCareRecord(
  summaryCareRecord: string | null | undefined,
  summaryCareRecordUpdatedAt?: string | null
): ParsedSCRData | null {
  if (!summaryCareRecord || summaryCareRecord === 'Summary Care Record not found') {
    return null
  }

  try {
    const parsedScrData = JSON.parse(summaryCareRecord) as unknown
    if (typeof parsedScrData !== 'object' || parsedScrData === null) {
      return null
    }

    // Extract lastUpdated from nested structure if present
    const scrLastUpdated = (parsedScrData as { summaryCareRecord?: { lastUpdated?: string | null } })?.summaryCareRecord
      ?.lastUpdated

    // Priority: summaryCareRecordUpdatedAt > nested lastUpdated
    let lastUpdated: Date | null = null
    if (summaryCareRecordUpdatedAt) {
      lastUpdated = new Date(summaryCareRecordUpdatedAt)
    } else if (scrLastUpdated) {
      lastUpdated = new Date(scrLastUpdated)
    }

    return {
      ...parsedScrData,
      lastUpdated
    } as ParsedSCRData
  } catch {
    return null
  }
}
